import React from 'react'
import * as rn from 'react-native'

import Icon from 'react-native-vector-icons/MaterialCommunityIcons'

import * as c from '../../common'

import * as gs from '../gStyles'

import Labeled from './Labeled'
import Text from './Text'

export interface QtyInputProps {
  disabled?: boolean
  grow?: boolean
  guideline?: string
  guidelineType?: c.Guideline['type']
  hide?: boolean
  label?: false | string
  on: c.Surface
  onChange: (newValue: number) => void
  value?: number
}

export default React.memo<QtyInputProps>(function QtyInput({
  disabled,
  grow,
  guideline,
  guidelineType,
  hide,
  label,
  on,
  onChange,
  value = 0,
}) {
  const _colorScheme = rn.useColorScheme()
  const colorScheme = _colorScheme === 'unspecified' ? 'light' : _colorScheme
  const styles = gs.useThemedStyleSheet(themedStyles, on)

  const handleMinus = React.useCallback(() => {
    if (value > 0) {
      onChange(value - 1)
    }
  }, [onChange, value])
  const handlePlus = React.useCallback(() => {
    onChange(value + 1)
  }, [onChange, value])

  const rootEl = (
    <rn.View
      style={(() => {
        if (!grow && !label) return hide ? styles.rootHidden : styles.root
        return hide ? styles.rootGrowHidden : styles.rootGrow
      })()}
    >
      <rn.Pressable disabled={disabled} onPress={handleMinus}>
        <rn.View style={disabled ? styles.buttonDisabled : styles.button}>
          <Icon
            color={c.themeTuple[colorScheme][on].color as string}
            name="minus"
            size={20}
            style={disabled ? gs.disabled : gs.userSelectNone}
          />
        </rn.View>
      </rn.Pressable>

      <Text style={disabled ? styles.textDisabled : styles.text}>{value}</Text>

      <rn.Pressable disabled={disabled} onPress={handlePlus}>
        <rn.View style={disabled ? styles.buttonDisabled : styles.button}>
          <Icon
            color={c.themeTuple[colorScheme][on].color as string}
            name="plus"
            size={20}
            style={disabled ? gs.disabled : gs.userSelectNone}
          />
        </rn.View>
      </rn.Pressable>
    </rn.View>
  )

  if (label) {
    return (
      <Labeled
        disabled={disabled}
        grow={grow}
        guideline={guideline}
        guidelineType={guideline ? guidelineType : 'warning'}
        label={label}
        on={on}
      >
        {rootEl}
      </Labeled>
    )
  }

  return rootEl
})

const themedStyles = gs.ThemedStyleSheet.create((t, on, colorScheme) => {
  const flippedColorScheme = colorScheme === 'dark' ? 'light' : 'dark'

  const button = {
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    backgroundColor: c.themeTuple[flippedColorScheme][on].color,
    borderRadius: Number.MAX_SAFE_INTEGER,
    justifyContent: 'center' as const,
    padding: 12,
  }

  const text = {
    color: t[on].color,
    fontSize: 22,
    fontFamily: t[on].fontFamily,
    textAlign: 'center' as const,
    textAlignVertical: 'center' as const,
    ...gs.userSelectNone,
  }

  return {
    buttonDisabled: { ...button, ...gs.disabled },
    button,
    rootGrow: { ...gs.inputGrowNew, ...gs.row, ...gs.rowSpaceBetween100 },
    rootGrowHidden: { ...gs.inputGrowNewHidden, ...gs.row },
    root: { ...gs.inputShrinkNew, ...gs.rowSpaceBetween },
    rootHidden: { ...gs.inputShrinkNewHidden, ...gs.row },
    text,
    textDisabled: { ...text, ...gs.disabled },
  }
})
